<template>
  <div v-if="currentApparatusDetails">
    <div v-if="showButton">
      <button
        type="button"
        class="btn btn-outline-danger rounded-pill mt-4"
        @click="checkApparatusSubscription()"
        data-test-hook="remove-apparatus-button"
      >
        <i class="fas fa-trash-alt colorOrange"></i>
        {{ $t('xRemoveFromAccount') }}
      </button>
    </div>

    <Modal :modalId="'removeApparatusFromHomePremiumActivePlan-' + apparatusId" size="modal-lg">
      <ModalHeader :title="$t('xRemoveFromAccount')" icon="fas fa-exclamation-triangle colorOrange">
        <div class="float-right">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeAllModals()"
            data-test-hook="x-button-removeApparatusFromHomePremiumActivePlan"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div v-if="isGenerator()">
          <div class="row">
            <div class="col">
              {{ $t('xRemoveGeneratorFromHomePlanBodyText') }}
            </div>
          </div>
          <div class="row mt-4">
            <div class="col">
              {{ $t('xRemoveGeneratorDowngradeBodyText') }}
            </div>
          </div>
          <div class="row mt-4 mb-2">
            <div class="col">
              {{ $t('xRemoveGeneratorFromHomePlanConfirmationBodyText') }}
            </div>
          </div>
        </div>
        <div v-else-if="isTank()">
          <div class="row">
            <div class="col">
              {{ $t('xRemoveTankFromHomePlanBodyText') }}
            </div>
          </div>
          <div class="row mt-4">
            <div class="col">
              {{ $t('xRemoveTankDowngradeBodyText') }}
            </div>
          </div>
          <div class="row mt-4 mb-2">
            <div class="col">
              {{ $t('xRemoveTankFromHomePlanConfirmationBodyText') }}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeAllModals()"
          data-test-hook="no-do-not-remove-generator-button"
        >
          {{ isGenerator() ? $t('xKeepGeneratorButtonText') : isTank() ? $t('xKeepTankButtonText') : '' }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="removeApparatusFromSubAndRemoveApparatus()"
          data-test-hook="yes-remove-generator-button"
        >
          {{ isGenerator() ? $t('xRemoveGeneratorButtonText') : isTank() ? $t('xRemoveTankButtonText') : '' }}
          <span class="spinner-border spinner-border-sm" v-if="removing"></span>
        </button>
      </ModalFooter>
    </Modal>

    <Modal :modalId="'cancelSingleApparatusActivePlan-' + apparatusId" size="modal-lg">
      <ModalHeader :title="$t('xCancelPlan')" icon="far fa-exclamation-triangle colorOrange">
        <div class="float-right">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeAllModals()"
            data-test-hook="x-button-cancelSingleApparatusActivePlan"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="row">
          <div v-if="isGenerator()" class="col">
            {{ $t('xCancelSingleGeneratorPlanBodyText') }}
          </div>
          <div v-if="isTank()" class="col">
            {{ $t('xCancelSingleTankPlanBodyText') }}
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            {{ $t('xNoPartialRefundsBodyText') }}
            {{ getSubscriptionRenewalDate() }}.
          </div>
        </div>
        <div class="row mt-4 mb-2">
          <div class="col">
            {{ $t('xCancelPlanConfirmationBodyText') }}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeAllModals()"
          data-test-hook="no-do-not-cancel-plan-button"
        >
          {{ $t('xKeepSingleApparatusPlanButtonText') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="cancelPlanAndRemoveApparatusWithActivePlanFromAccount()"
          data-test-hook="yes-cancel-plan-button"
        >
          {{ $t('xCancelSingleApparatusPlanButtonText') }}
          <span class="spinner-border spinner-border-sm" v-if="removing"></span>
        </button>
      </ModalFooter>
    </Modal>

    <div class="modal" :id="'removeApparatusWithInactivePlanModal-' + apparatusId" role="dialog">
      <div class="modal-dialog modal-dialog-centered modal-lg" style="width: 560px;">
        <div class="modal-content">
          <ModalHeader :title="$t('xRemoveFromAccount')" icon="far fa-exclamation-triangle colorOrange">
            <div class="float-right">
              <button
                type="button"
                class="btn btn-icon-button btn-icon-button--gray"
                @click="closeAllModals()"
                data-test-hook="x-button-removeApparatusWithInactivePlanModal"
              >
                <i class="fas fa-times" />
              </button>
            </div>
          </ModalHeader>
          <ModalBody>
            <div v-if="isGenerator()">
              <div class="row">
                <div class="col">
                  {{ $t('xRemoveGeneratorLoseAccessBodyText') }}
                </div>
              </div>
              <div class="row mt-4 mb-2">
                <div class="col">
                  {{ $t('xRemoveGeneratorConfirmationBodyText') }}
                </div>
              </div>
            </div>
            <div v-else-if="isTank()">
              <div class="row">
                <div class="col">
                  {{ $t('xRemoveTankLoseAccessBodyText') }}
                </div>
              </div>
              <div class="row mt-4 mb-2">
                <div class="col">
                  {{ $t('xRemoveTankConfirmationBodyText') }}
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeAllModals()"
              data-test-hook="no-do-not-remove-generator-button"
            >
              {{ isGenerator() ? $t('xKeepGeneratorButtonText') : isTank() ? $t('xKeepTankButtonText') : '' }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="removeApparatusWithInactivePlanFromAccount()"
              data-test-hook="yes-remove-generator-button"
            >
              {{ isGenerator() ? $t('xRemoveGeneratorButtonText') : isTank() ? $t('xRemoveTankButtonText') : '' }}
              <span class="spinner-border spinner-border-sm" v-if="removing"></span>
            </button>
          </ModalFooter>
        </div>
      </div>
    </div>

    <Modal :modalId="'planOnMobilePlatform-' + apparatusId" size="modal-md">
      <ModalHeader :title="$t('xActionRequired')" icon="far fa-exclamation-triangle colorOrange">
        <div class="float-right">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeAllModals()"
            data-test-hook="x-button-planOnMobilePlatform"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="row">
          <div v-if="activePlanOnGoogle" class="col">
            {{ $t('xRemoveProductFromGoogle') }}
          </div>
          <div v-else-if="activePlanOnApple" class="col">
            {{ $t('xRemoveProductFromApple') }}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          class="btn btn-primary"
          @click="closeAllModals()"
          data-test-hook="no-do-not-remove-generator-button"
        >
          {{ $t('xDone') }}
        </button>
      </ModalFooter>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as Constants from '@/constants'
import {
  getSubscriptionForApparatus,
  getLegacySubscriptionForApparatus,
  openModal,
  closeModal,
  isHomePremiumSubscription,
  isSingleApparatusSubscription,
  displayShortDate,
  planEligibleForDowngrade
} from '@/utils/mixins'

export default {
  props: ['showButton', 'apparatusId', 'checkSubscription'],
  data() {
    return {
      removing: false,
      activePlanOnGoogle: false,
      activePlanOnApple: false,
      subscriptionDetails: null,
      apparatusSubscription: null,
      apparatusLegacySubscription: null
    }
  },
  computed: {
    ...mapState({
      currentApparatusDetails: state => state.apparatus.currentApparatusDetails,
      accountProfile: state => state.account.accountProfile
    })
  },
  methods: {
    isGenerator() {
      return this.currentApparatusDetails.apparatusClassification === Constants.HsbApparatusType
    },
    isTank() {
      return this.currentApparatusDetails.apparatusClassification === Constants.FuelTankApparatusType
    },
    async fetchSubscriptionDetails() {
      await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .then(response => {
          this.subscriptionDetails = response.data
          this.apparatusSubscription = getSubscriptionForApparatus(
            this.apparatusId,
            this.subscriptionDetails?.subscriptions
          )
          this.apparatusLegacySubscription = getLegacySubscriptionForApparatus(
            this.apparatusId,
            this.subscriptionDetails?.legacyBillingAccount
          )
        })
        .catch(() => {
          this.throwRemoveErrorToast()
          this.closeAllModals()
        })
    },
    getSubscriptionRenewalDate() {
      if (this.apparatusSubscription)
        return this.$moment(this.apparatusSubscription.renewalDate).format(this.$t('xShortDate'))
      else if (this.apparatusLegacySubscription)
        return this.$moment(this.apparatusLegacySubscription.nextBillingDate).format(this.$t('xShortDate'))
    },
    checkApparatusSubscription() {
      if (
        (this.apparatusSubscription && this.isActiveOrPendingChangeSubscription()) ||
        (this.apparatusLegacySubscription && this.apparatusLegacySubscription.status === Constants.SubscriptionActive)
      ) {
        if (this.apparatusSubscription?.store === Object.values(Constants.SubscriptionStoreTypes).indexOf('Google')) {
          this.activePlanOnGoogle = true
          this.openModal('planOnMobilePlatform-' + this.apparatusId)
        } else if (
          this.apparatusSubscription?.store === Object.values(Constants.SubscriptionStoreTypes).indexOf('Apple')
        ) {
          this.activePlanOnApple = true
          this.openModal('planOnMobilePlatform-' + this.apparatusId)
        } else if (this.apparatusSubscription?.apparatuses?.length > 1) {
          this.openModal('removeApparatusFromHomePremiumActivePlan-' + this.apparatusId)
        } else if (this.apparatusSubscription?.apparatuses?.length === 1 || this.apparatusLegacySubscription) {
          this.openModal('cancelSingleApparatusActivePlan-' + this.apparatusId)
        }
      } else {
        this.openModal('removeApparatusWithInactivePlanModal-' + this.apparatusId)
      }
    },
    isActiveOrPendingChangeSubscription() {
      return (
        this.apparatusSubscription.status === Constants.SubscriptionDetailActive ||
        this.apparatusSubscription.status === Constants.SubscriptionDetailPendingProductChange
      )
    },
    async removeApparatusFromAccount() {
      await this.$store
        .dispatch('removeApparatus', {
          organizationId: this.accountProfile.organizationId,
          apparatusId: this.apparatusId
        })
        .then(async () => {
          await this.fetchSubscriptionDetails()
          let removedApparatusPlan = this.subscriptionDetails.subscriptions?.find(
            subscription => subscription.store !== Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
          )
          if (window.location.pathname.includes('dashboard')) window.location.reload()
          else this.$router.push('/dashboard')
          if (removedApparatusPlan?.status === Constants.SubscriptionDetailActive) {
            this.$store.dispatch('displayToast', {
              title:
                this.planEligibleForDowngrade(removedApparatusPlan) && this.isGenerator()
                  ? this.$t('xGeneratorRemovedSuccessfully')
                  : this.planEligibleForDowngrade(removedApparatusPlan) && this.isTank()
                  ? this.$t('xFuelMonitorRemovedSuccessfully')
                  : this.$t('xSuccess'),
              message: this.planEligibleForDowngrade(removedApparatusPlan)
                ? this.$t('xYourPlanEligibleForDowngrade')
                : this.isGenerator()
                ? this.$t('xGeneratorRemovedSuccessfully')
                : this.isTank()
                ? this.$t('xFuelMonitorRemovedSuccessfully')
                : '',
              variant: 'success'
            })
          } else {
            this.$store.dispatch('displayToast', {
              title: this.$t('xSuccess'),
              message: this.isGenerator()
                ? this.$t('xGeneratorRemovedSuccessfully')
                : this.isTank()
                ? this.$t('xFuelMonitorRemovedSuccessfully')
                : '',
              variant: 'success'
            })
          }
        })
        .catch(() => {
          this.throwRemoveErrorToast()
        })
        .finally(() => {
          this.removing = false
          this.closeAllModals()
        })
    },
    async goToPlanDetailsToCancelSubscription() {
      this.$router.push({ name: 'settingsSubscriptions', query: { managePlan: true } })
    },
    async removeApparatusFromSubAndRemoveApparatus() {
      this.removing = true
      let subscriptionApparatusList = this.apparatusSubscription.apparatuses
        .map(subscriptionApparatus => {
          return subscriptionApparatus.apparatusId
        })
        .filter(apparatusId => apparatusId !== this.apparatusId)
      await this.$store
        .dispatch('updateSubscriptionApparatuses', {
          organizationId: this.accountProfile.organizationId,
          store: this.apparatusSubscription.store,
          productId: this.apparatusSubscription.product.productId,
          apparatusIds: subscriptionApparatusList
        })
        .then(async () => {
          // If plan is pending change, need to remove from both current and pending change plans
          if (this.apparatusSubscription.status === Constants.SubscriptionDetailPendingProductChange) {
            await this.$store.dispatch('updateSubscriptionApparatuses', {
              organizationId: this.accountProfile.organizationId,
              store: this.apparatusSubscription.store,
              productId: this.apparatusSubscription.pendingProduct.productId,
              apparatusIds: subscriptionApparatusList
            })
          }
          this.removeApparatusFromAccount()
        })
        .catch(() => {
          this.throwRemoveErrorToast()
          this.removing = false
          this.closeAllModals()
        })
    },
    async removeApparatusWithInactivePlanFromAccount() {
      this.removing = true
      this.removeApparatusFromAccount()
    },
    async cancelPlanAndRemoveApparatusWithActivePlanFromAccount() {
      if (this.apparatusSubscription || this.apparatusLegacySubscription) {
        this.goToPlanDetailsToCancelSubscription()
      }
    },
    throwRemoveErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSomethingWentWrong'),
        message: this.$t('xPleaseWaitContactSupport'),
        variant: 'danger'
      })
    },
    closeAllModals() {
      this.$emit('closeEnrollmentDecisionModal')
      this.closeModal('removeApparatusWithInactivePlanModal-' + this.apparatusId)
      this.closeModal('removeApparatusFromHomePremiumActivePlan-' + this.apparatusId)
      this.closeModal('cancelSingleApparatusActivePlan-' + this.apparatusId)
      this.closeModal('planOnMobilePlatform-' + this.apparatusId)
    }
  },
  watch: {
    currentApparatusDetails: async function(newVal, oldVal) {
      if (newVal && this.accountProfile) {
        await this.fetchSubscriptionDetails()
      }
    },
    checkSubscription: function(newVal, oldVal) {
      if (newVal === true) {
        this.checkApparatusSubscription()
      }
    }
  },
  async beforeMount() {
    //do this here so it happens before the onClick checkApparatusSubscription
    if (this.accountProfile && this.currentApparatusDetails) {
      await this.fetchSubscriptionDetails()
    } else {
      this.$store.dispatch('getAccountProfile')
    }
  },
  async mounted() {
    if (this.apparatusId) {
      this.$appInsights.trackEvent({
        name: 'getApparatusDetailsCalled',
        properties: {
          location: 'RemoveApparatus.vue > mounted',
          organizationId: this.accountProfile?.organizationId,
          apparatusId: this.apparatusId
        }
      })
      await this.$store.dispatch('getApparatusDetails', this.apparatusId)
    }
  }
}
</script>
